import { createQueryKeys } from '@lukemorales/query-key-factory'

import type { UseQueryOptionsWithoutQueryFn } from '~/types/util/tanstack-query'

import { type ParametersGetAll, responseDataTypeName, useFetchFavourite } from './useFetchFavourite'

export const queryKeys = createQueryKeys(responseDataTypeName, {
  all: ({
    params,
    workspaceId,
  }:
  {
    params?: MaybeRef<ParametersGetAll>
    workspaceId: MaybeRef<string>
  },
  ) => ({
    queryKey: [
      workspaceId,
      ...(params ? [params] : []),
    ],
  }),
  allUnpaginated: ({
    workspaceId,
  }:
  {
    workspaceId: MaybeRef<string>
  },
  ) => ({
    queryKey: [
      workspaceId,
    ],
  }),
})

const all = ({
  params,
  useQueryOptions = {},
}: {
  params?: MaybeRef<ParametersGetAll>
  useQueryOptions?: UseQueryOptionsWithoutQueryFn
} = {}) => {
  const { getAll } = useFetchFavourite()
  const { activeWorkspaceId } = useAuth()

  return useQuery({
    ...queryKeys.all({
      params,
      workspaceId: activeWorkspaceId as MaybeRef<string>,
    }),
    queryFn: () => getAll({
      params: unref(params),
      workspaceId: unref(activeWorkspaceId) || '',
    }),
    enabled: () => !!unref(activeWorkspaceId),
    ...useQueryOptions,
  })
}

const allUnpaginated = ({
  useQueryOptions = {},
}: {
  useQueryOptions?: UseQueryOptionsWithoutQueryFn
} = {}) => {
  const { getAllUnpaginated } = useFetchFavourite()
  const { activeWorkspaceId } = useAuth()

  return useQuery({
    ...queryKeys.allUnpaginated({
      workspaceId: activeWorkspaceId as MaybeRef<string>,
    }),
    queryFn: async () => {
      const firstPage = await getAllUnpaginated({
        workspaceId: unref(activeWorkspaceId) || '',
      })

      if (firstPage.meta?.pagination?.total_pages && firstPage.meta.pagination.total_pages > 1) {
        const remainingPages = await Promise.all(
          Array.from({ length: firstPage.meta.pagination.total_pages }).slice(1).map((_, i) => i + 2)
            .map(page => getAllUnpaginated({
              workspaceId: unref(activeWorkspaceId) || '',
              params: {
                page,
              },
            })),
        )

        return {
          data: firstPage.data.concat(remainingPages.map(r => r.data).flat()),
          included: [...(firstPage.included ?? [])].concat(remainingPages.map(r => r.included ?? []).flat()),
        }
      }

      return {
        data: firstPage.data,
        included: firstPage.included,
      }
    },
    enabled: () => !!unref(activeWorkspaceId),
    ...useQueryOptions,
  })
}

export const useQueryFavourite = {
  all,
  allUnpaginated,
}
